import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { sidebarNavigation } from '../sidebarNavigation';
import useNavMenuFeatures from '@app/hooks/useNavMenuFeatures';
import {
  FeatureGroups,
  SidebarNavigationItem,
  SideNavSubFeatures,
  SiderContentProps,
} from '@app/interfaces/sideNavigation';

import * as S from './SiderMenu.styles';

const sidebarNavFlat = sidebarNavigation
  .reduce(
    (result: SidebarNavigationItem[], current) =>
      result.concat(current.children && current.children.length > 0 ? current.children : current),
    [],
  )
  .filter(
    (item, index, self) =>
      // Deduplicate by key to ensure max 1 entry per key
      index === self.findIndex((i) => i.key === item.key),
  );

const matchesPath = (url: string | undefined, pathname: string) =>
  !!url && (pathname === url || pathname.startsWith(url + '/'));

const SiderMenu: React.FC<SiderContentProps> = ({ setCollapsed }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const features = useNavMenuFeatures();

  const currentMenuItem = sidebarNavFlat.find(({ url }) => matchesPath(url, location.pathname));
  const selectedKeys = currentMenuItem ? [currentMenuItem.key] : [];

  const openedSubmenu = sidebarNavigation.find(({ children }) =>
    children?.some(({ url }) => matchesPath(url, location.pathname)),
  );
  const openKeys = openedSubmenu ? [openedSubmenu.key] : [];

  const [menuOpenKeys, setMenuOpenKeys] = React.useState<string[]>(openKeys);

  React.useEffect(() => {
    setMenuOpenKeys((prev) => {
      const next = openKeys.filter((k) => !prev.includes(k));
      return next.length ? [...prev, ...next] : prev;
    });
  }, [location.pathname]);

  const featuresGroupedByCategory: FeatureGroups = sidebarNavigation.reduce<FeatureGroups>((acc, { key, children }) => {
    if (children) {
      acc[key] = children.reduce<SideNavSubFeatures>((acc, childNav) => {
        const nameKey = `${childNav.key}Feature`;
        const feature = features[nameKey];

        return feature ? { ...acc, [nameKey]: feature } : acc;
      }, {});
    } else {
      const nameKey = `${key}Feature`;

      acc[key] = features[nameKey] ? { [nameKey]: features[nameKey] } : {};
    }

    return acc;
  }, {});

  const visibleTopNavItems = sidebarNavigation.filter((navItem) => {
    const childrenGroup = featuresGroupedByCategory[navItem.key];
    const hasVisibleChildren =
      childrenGroup &&
      Object.values(featuresGroupedByCategory[navItem.key]).some((childFeature) => childFeature?.visible);

    return hasVisibleChildren;
  });

  return (
    <S.Menu
      mode="inline"
      selectedKeys={selectedKeys}
      openKeys={menuOpenKeys}
      onOpenChange={(keys) => setMenuOpenKeys(keys as string[])}
      onClick={() => setCollapsed(true)}
      items={visibleTopNavItems.map((nav) => {
        const isSubMenu = nav.children?.length;

        return {
          key: nav.key,
          title: t(nav.title),
          label: isSubMenu ? t(nav.title) : <Link to={nav.url || ''}>{t(nav.title)}</Link>,
          icon: nav.icon,
          children:
            isSubMenu &&
            nav.children &&
            nav.children
              .filter((childNav) => {
                const feature = featuresGroupedByCategory[nav.key][`${childNav.key}Feature`];
                const tableView = feature?.views?.find((view) => view.name === 'tableView');

                return feature?.visible && (tableView ? tableView?.visible : true);
              })
              .filter(
                (childNav, index, self) =>
                  // Deduplicate by key to ensure max 1 entry per key
                  index === self.findIndex((item) => item.key === childNav.key),
              )
              .map((childNav) => ({
                key: childNav.key,
                label: <Link to={childNav.url || ''}>{t(childNav.title)}</Link>,
                title: t(childNav.title),
              })),
        };
      })}
    />
  );
};

export default SiderMenu;
